import React, { useMemo } from "react";
import { useIntl } from "react-intl";
import { Entity, Page } from "@/model";
import entityRepository from "@/repository/entityRepository";
import { useDeleteEntity } from "@/query/entity";
import { useAgentId, useDialog, useSelection } from "@/hook";
import { IndeterminateCheckbox } from "@/component/input";
import SelectionActionBar from "@/component/SelectionActionBar";
import { errorToast, successToast } from "@/component/Toast";
import SortButton from "@/component/SortBurtton";

import { EntityPageParam } from "../EntityView";
import EntityFragment from "./EntityFragment";


interface EntityTableProps {
    page?: Page<Entity>;
    pageParam: EntityPageParam;
    onClickSortHead: (type: 'name' | 'type') => void;
}

const EntityTable: React.FC<EntityTableProps> = (props) => {
    const { page, pageParam, onClickSortHead } = props;
    
    const intl = useIntl();
    const agentId = useAgentId();   
    const dialog = useDialog();

    const { mutateAsync: deleteEntity } = useDeleteEntity(agentId);

    const pageIds = useMemo(() => page?.content.map(entity => entity.id) ?? [], [page?.content]);

    const { selectionCount, isAllSelected, pageSelectionStatus, selectedIds, markOne, markPage, markAll, clearSelection } = useSelection({
        targetIds: pageIds,
        total: page?.total ?? 0,
    });

    const deleteByIds = async (ids: string[]) => {
        await deleteEntity({ ids: ids })
            .then(() => {
                clearSelection();
                successToast(intl.formatMessage({ id: "t005" }));
            })
            .catch(() => {
                errorToast(intl.formatMessage({ id: "t006" }));
            });
    };

    const deleteAll = async () => {
        const params = {
            keyword: pageParam.searchKeyword,
            searchType: pageParam.searchType,
            optionList: pageParam.typeParams,
        };
        await deleteEntity(params)
            .then(() => {
                clearSelection();
                successToast(intl.formatMessage({ id: "t005" }));
            })
            .catch(() => {
                errorToast(intl.formatMessage({ id: "t006" }));
            });
    };

    const onClickDeleteActionBtn = (e: React.MouseEvent) => {
        e.preventDefault();

        dialog.open({
            title: intl.formatMessage({ id: "i05022" }),
            content: intl.formatMessage({ id: "i05014" }),
            onConfirm: () => isAllSelected ? deleteAll() : deleteByIds(selectedIds),
            confirmText: intl.formatMessage({ id: "b0008" }),
            variant: "danger",
        });
    };

    const onClickDownload = () => {
        if (isAllSelected) {
            entityRepository.export(agentId, {
                keyword: pageParam.searchKeyword,
                searchType: pageParam.searchType,
                optionList: pageParam.typeParams,
            });
        } else {
            entityRepository.export(agentId, {
                ids: selectedIds,
            });
        }
    };

    return (
        <div role="table"
             className="selection-table grid-table-entity"
        >
            <div role="rowgroup"
                 className="selection-table-thead"
            >
                <div role="columnheader"
                     className="selection-table-th"
                >
                    <IndeterminateCheckbox status={pageSelectionStatus}
                                           onChange={markPage}
                    />
                </div>
                <div role="columnheader"
                     className="selection-table-th group"
                >
                    <SortButton order={pageParam.sortBy.order}
                                active={pageParam.sortBy.type === "name"}    
                                onClick={() => onClickSortHead("name")}
                    >
                        {intl.formatMessage({ id: "i05002" })}
                    </SortButton>
                </div>
                <div role="columnheader"
                     className="selection-table-th group pl-[15px]"
                >
                    <SortButton order={pageParam.sortBy.order}
                                active={pageParam.sortBy.type === "type"}    
                                onClick={() => onClickSortHead("type")}
                    >
                        {intl.formatMessage({ id: "i05003" })}
                    </SortButton>
                </div>
                <div role="columnheader"
                     className="selection-table-th"
                />
            </div>
            <div role="rowgroup"
                 className="selection-table-tbody"
            >
                {page?.content.map((entity: Entity) => (
                    <EntityFragment key={entity.id}
                                    entity={entity}
                                    selectedIds={selectedIds}
                                    searchQuery={pageParam.searchKeyword}
                                    searchType={pageParam.searchType}
                                    onDelete={id => deleteByIds([id])}
                                    onCheck={e => markOne(entity.id, e.currentTarget.checked)}
                    />
                ))}
                {!page?.content?.length && (
                    <div className="empty-table">
                        {intl.formatMessage({ id: pageParam.searchKeyword ? "b0014" : "b0018" })}
                    </div>
                )}
            </div>

            <div className="col-span-full">
                <SelectionActionBar centerByParent
                                    selectionCount={selectionCount}
                                    selectAll={() => markAll(true)}
                                    deSelect={() => markAll(false)}
                                    remove={onClickDeleteActionBtn}
                                    download={onClickDownload}
                />
            </div>
        </div>
    );
};

export default EntityTable;